import { Component, inject, OnInit } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { ErrorHandlerService } from '../../../../../_shared/services/error-handler.service';
import { ExecutiveStatsService } from '../../../services/executive-stats.service';
import { differenceInDays } from 'date-fns';
import { TimeSeriesGroupingHelper } from '../../../../../_shared/helpers/chart-grouping/time-series-grouping.helper';
import { EmptyStateComponent } from '../../../../../_shared/components/states/empty-state/empty-state.component';
import { Subject } from 'rxjs';
import { GraphTrendIconComponent } from '../../../../../_shared/components/graphs/graph-trend-icon/graph-trend-icon.component';
import { AccountFilterComponent } from '../../../../../_shared/components/filters/account-filter/account-filter.component';
import { DateFilterComponent } from '../../../../../_shared/components/filters/date-filter/date-filter.component';
import { AccountFilterData, DateIntervalFilterData, DriverFilterData } from '../../../../../_shared/models';
import { isEmptyObject } from '../../../../../_shared/helpers/object.helper';
import { MultiLineChartComponent, MultiLineChartDataPoint, MultiLineChartSeries } from '../../../../../_shared/components/graphs/multi-line-chart/multi-line-chart.component';
import { ExecutiveStatsRequest } from '../../../../../_shared/models/driver-summary-request.model';
import { GraphErrorStateComponent } from '../../../../../_shared/components/graphs/graph-error-state/graph-error-state.component';
import { TranslocoDirective, TranslocoPipe, TranslocoService } from '@jsverse/transloco';

@Component({
  selector: 'app-distance-driven-modal',
  standalone: true,
  imports: [MultiLineChartComponent, EmptyStateComponent, GraphTrendIconComponent, AccountFilterComponent, DateFilterComponent, GraphErrorStateComponent, TranslocoDirective, TranslocoPipe],
  templateUrl: './distance-driven-modal.component.html',
  styleUrl: './distance-driven-modal.component.scss'
})
export class DistanceDrivenModalComponent implements OnInit {
  private activeModal = inject(NgbActiveModal);
  private executiveStatsService = inject(ExecutiveStatsService);
  private errorHandlerService = inject(ErrorHandlerService);
  private transloco = inject(TranslocoService);

  chartData: MultiLineChartDataPoint[] = [];
  readonly chartHeight = 335;
  private readonly defaultYTicks = [100, 80, 60, 40, 20, 0];
  yTicks: number[] = this.defaultYTicks;
  readonly series: MultiLineChartSeries[] = [
    { key: 'KmDrivenPerDay', color: '#EE2737', label: this.transloco.translate('executiveStats.chart.kmDrivenPerDay'), showDots: false },
    { key: 'Globaltrends', color: '#3873C9', label: this.transloco.translate('executiveStats.chart.globalTrends'), showDots: false }
  ];

  isLoading: boolean = true;
  hasError: boolean = false;
  accountFilter: AccountFilterData;
  dateFilter: DateIntervalFilterData;
  driverFilter: DriverFilterData;
  totalValue: number = 0;
  percentageChange: number = 0;
  private cancelSubject: Subject<void> = new Subject<void>();

  constructor() {
    this.accountFilter = {} as AccountFilterData;
    this.dateFilter = {} as DateIntervalFilterData;
    this.driverFilter = {} as DriverFilterData;
  }

  async ngOnInit(): Promise<void> {
    if (!isEmptyObject(this.dateFilter) && !isEmptyObject(this.accountFilter)) {
      await this.loadData();
    }
  }

  async onAccountFilterChange(filter: AccountFilterData): Promise<void> {
    this.accountFilter = filter;
    this.updateFilter();
  }

  async onDateFilterChange(filter: DateIntervalFilterData): Promise<void> {
    this.dateFilter = filter;
    this.updateFilter();
  }

  async updateFilter(): Promise<void> {
    if (!isEmptyObject(this.dateFilter) && !isEmptyObject(this.accountFilter)) {
      await this.loadData();
    }
  }

  async loadData(): Promise<void> {
    this.isLoading = true;
    this.hasError = false;

    try {
      const request: ExecutiveStatsRequest = {
        driverIds: this.driverFilter.driverIds ?? [],
        accountIds: this.accountFilter.accountIds ?? [],
        timeFrom: this.dateFilter.startDate!,
        timeTo: this.dateFilter.endDate!,
        interval: this.dateFilter.interval
      };

      this.cancelSubject.next();
      const [distanceDriven, globalDistanceDriven] = await Promise.all([
        this.executiveStatsService.getDistanceDriven(request, this.cancelSubject),
        this.executiveStatsService.getGlobalDistanceDriven(request, this.cancelSubject)
      ]);

      this.chartData = this.processData(
        distanceDriven.data || [],
        globalDistanceDriven.data || []
      );
      this.yTicks = this.calculateYTicks(this.chartData);

      if (this.chartData.length > 0) {
        const lastPeriod = this.chartData[this.chartData.length - 1];
        this.totalValue = this.series.reduce((sum, s) => sum + (Number(lastPeriod[s.key]) || 0), 0);

        if (this.chartData.length > 1) {
          const previousPeriod = this.chartData[this.chartData.length - 2];
          const previousValue = this.series.reduce((sum, s) => sum + (Number(previousPeriod[s.key]) || 0), 0);
          if (previousValue > 0) {
            this.percentageChange = ((this.totalValue - previousValue) / previousValue) * 100;
          }
        }
      }

      this.isLoading = false;
    } catch (error) {
      this.errorHandlerService.handleError(error, {
        callback: () => {
          this.hasError = true;
          this.isLoading = false;
          this.chartData = [];
        }
      });
    }
  }

  private processData(distanceData: any[], globalData: any[]): MultiLineChartDataPoint[] {
    if (!distanceData.length && !globalData.length) return [];

    const datasets = [
      { data: distanceData.map(x => ({ totalValue: x.totalValue, timeSeries: new Date(x.timeSeries) })), label: 'KmDrivenPerDay' },
      { data: globalData.map(x => ({ totalValue: x.totalValue, timeSeries: new Date(x.timeSeries) })), label: 'Globaltrends' }
    ];

    const daysDifference = differenceInDays(this.dateFilter.endDate!, this.dateFilter.startDate!);

    if (daysDifference === 0) {
      return TimeSeriesGroupingHelper.groupByHours(datasets, true);
    } else if (daysDifference <= 31) {
      return TimeSeriesGroupingHelper.groupByDays(datasets, this.dateFilter.startDate!, this.dateFilter.endDate!);
    } else if (daysDifference > 60) {
      return TimeSeriesGroupingHelper.groupByMonths(datasets, this.dateFilter.startDate!, this.dateFilter.endDate!);
    } else {
      return TimeSeriesGroupingHelper.groupByWeeks(datasets, this.dateFilter.startDate!, this.dateFilter.endDate!);
    }
  }

  private calculateYTicks(chartData: MultiLineChartDataPoint[]): number[] {
    if (!chartData?.length) return this.defaultYTicks;

    const allValues: number[] = [];
    chartData.forEach(point => {
      this.series.forEach(s => {
        const value = Number(point[s.key]) || 0;
        if (value > 0) allValues.push(value);
      });
    });

    if (!allValues.length) return this.defaultYTicks;

    const max = Math.max(...allValues);
    const roundedMax = Math.max(
      Math.ceil(max / 10) * 10,
      Math.ceil(max * 1.1 / 10) * 10
    );

    const tickInterval = roundedMax / 5;
    const ticks: number[] = [];
    for (let i = 0; i <= 5; i++) {
      ticks.push(Math.round(tickInterval * i));
    }

    return ticks.sort((a, b) => b - a);
  }

  formatPercentChange(): string {
    const sign = this.percentageChange > 0 ? '+' : '';
    return `${sign}${this.percentageChange.toLocaleString('default', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}%`;
  }

  yAxisFormatter = (value: number): string => {
    return `${value.toLocaleString()} km`;
  }

  tooltipFormatter = (value: number): string => {
    return `${value.toLocaleString()} km`;
  }

  isUptrendPositive(): boolean {
    return true;
  }

  formatValue(): string {
    return `${this.totalValue.toLocaleString()} km`;
  }

  hasData(): boolean {
    if (!this.chartData || this.chartData.length === 0) return false;
    return this.chartData.some(point => {
      return this.series.some(s => (Number(point[s.key]) || 0) > 0);
    });
  }

  close(): void {
    this.activeModal.close();
  }
}